// Core Modules
import { ChangeDetectionStrategy, Component, input, model, output, signal } from '@angular/core';

// Models
import { ChipShape, ChipVariant } from '@shared/models/chip.model';

export type ChipSize = 'compact' | 'sm' | 'md';

@Component({
  selector: 'app-chip',
  templateUrl: './chip.component.html',
  styleUrl: './chip.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChipComponent {
  // Single-pill display mode
  /** Text shown inside the pill. Ignored when `value` is bound (tag-input mode). */
  public readonly label = input<string>('');
  /** Whether to show a ✕ remove button inside the pill. */
  public readonly removable = input<boolean>(false);
  /** Emitted when the remove button is clicked. Only fires when removable is true. */
  public readonly removed = output<string>();
  /** Colour variant. */
  public readonly variant = input<ChipVariant>('neutral');
  /** `sm` (default) is the small uppercase status-badge look; `md` is a larger normal-case pill for selection/tag lists; `compact` is a tight, small-text pill/chip for dense lists (e.g. a scrollable list of ZIP codes). */
  public readonly size = input<ChipSize>('sm');
  /** Border-radius treatment. See `ChipShape`. */
  public readonly shape = input<ChipShape>('pill');

  // Tag-input mode — bind `value` to render an editable list of chips (with a draft input field)
  // instead of a single pill.
  public readonly value = model<string[]>();
  public readonly placeholder = input<string>('');
  public readonly validate = input<(v: string) => boolean>(() => true);
  public readonly draft = signal('');

  public onRemove(event: MouseEvent): void {
    event.stopPropagation();
    this.removed.emit(this.label());
  }

  public onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      this.commit();
    } else if (event.key === 'Backspace' && this.draft() === '') {
      this.value.update((cur) => (cur ?? []).slice(0, -1));
    }
  }

  public onInput(event: Event): void {
    const raw = (event.target as HTMLInputElement).value;
    if (raw.includes(',')) {
      const parts = raw.split(',');
      const last = parts.pop() ?? '';
      const additions = parts.map((p) => p.trim()).filter((p) => p && this.validate()(p));
      if (additions.length) {
        this.value.update((cur) => this.merge(cur ?? [], additions));
      }
      this.draft.set(last);
    } else {
      this.draft.set(raw);
    }
  }

  public remove(index: number): void {
    this.value.update((cur) => (cur ?? []).filter((_, i) => i !== index));
  }

  public isInvalid(chip: string): boolean {
    return !this.validate()(chip);
  }

  /** Flushes any typed-but-uncommitted draft text into a chip — called on Enter/comma and on blur, so
   * a value read immediately after (e.g. a sibling "Validate" button) never misses in-progress input.
   * Text failing `validate()` (e.g. an incomplete ZIP) is left in the draft rather than committed. */
  public commit(): void {
    const v = this.draft().trim();
    if (!v || !this.validate()(v)) {
      return;
    }
    this.value.update((cur) => this.merge(cur ?? [], [v]));
    this.draft.set('');
  }

  /** Empties both the committed chips and any uncommitted draft text — for a "Clear all" action
   * rendered outside the field, which cannot reach `draft` on its own. */
  public clear(): void {
    this.value.set([]);
    this.draft.set('');
  }

  private merge(current: string[], additions: string[]): string[] {
    const seen = new Set(current);
    const out = [...current];
    for (const a of additions) {
      if (!seen.has(a)) {
        seen.add(a);
        out.push(a);
      }
    }
    return out;
  }
}
